// Server functions for Prompt operations
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { db } from '@/src/server/db'
import { auth } from '@/auth'
import { getSessionFromRequest } from '@/src/server/lib/accessControl'
import { z } from 'zod'
import { PERMISSIONS } from '@/permission.config'
import { hasPermission } from '@/src/server/lib/permissions'

// Input schemas
const ListPromptsInput = z.object({
  all: z.boolean().optional(),
}).optional()

const GetPromptByIdInput = z.object({
  id: z.string(),
})

const UpsertPromptInput = z.object({
  id: z.string().optional(),
  name: z.string(),
  description: z.string().optional(),
  prompt: z.string(),
  enabled: z.boolean().optional(),
  order: z.number().optional(),
  defaultAiModelId: z.string().nullable().optional(),
})

const DeletePromptInput = z.object({
  id: z.string(),
})

const ReorderPromptsInput = z.object({
  ids: z.array(z.string()),
})

// List prompts - if all=true, returns all prompts (including disabled), otherwise only enabled
export const listPrompts = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => ListPromptsInput.parse(data))
  .handler(async ({ data }) => {
    const prompts = await db.prompt.findMany({
      where: data?.all ? {} : { enabled: true },
      orderBy: { order: 'asc' },
      select: data?.all ? undefined : { 
        id: true, 
        name: true, 
        description: true, 
        enabled: true, 
        order: true, 
        defaultAiModelId: true,
        defaultAiModel: true,
      },
    })
    return prompts
  })

export const getPromptById = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetPromptByIdInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN, PERMISSIONS.PROMPTS_MANAGE])
    
    const prompt = await db.prompt.findUnique({ 
      where: { id: data.id },
      include: { defaultAiModel: true }
    })
    if (!prompt) throw new Error('Prompt not found')
    if (!prompt.enabled && !isAdmin) throw new Error('Prompt not found')
    return prompt
  })

export const upsertPrompt = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpsertPromptInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    
    // SECURITY: Only admins can modify prompts
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN, PERMISSIONS.PROMPTS_MANAGE])
    if (!isAdmin) {
      throw new Error('Unauthorized: Admin or Prompts Manager access required')
    }
    
    const { id, defaultAiModelId, ...rest } = data
    
    // Build prompt data, only include defaultAiModelId if it's a string
    const promptData: any = { ...rest }
    if (typeof defaultAiModelId === 'string') {
      promptData.defaultAiModelId = defaultAiModelId
    } else if (defaultAiModelId === null) {
      promptData.defaultAiModelId = null
    }

    if (id) {
      return await db.prompt.update({ where: { id }, data: promptData })
    } else {
      return await db.prompt.create({ data: promptData })
    }
  })

export const deletePrompt = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => DeletePromptInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    
    // SECURITY: Only admins can delete prompts
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN, PERMISSIONS.PROMPTS_MANAGE])
    if (!isAdmin) {
      throw new Error('Unauthorized: Admin or Prompts Manager access required')
    }
    
    const prompt = await db.prompt.findUnique({ where: { id: data.id } })
    if (!prompt) throw new Error('Prompt not found')
    return await db.prompt.delete({ where: { id: data.id } })
  })

export const reorderPrompts = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => ReorderPromptsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    
    // SECURITY: Only admins can reorder prompts
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN, PERMISSIONS.PROMPTS_MANAGE])
    if (!isAdmin) {
      throw new Error('Unauthorized: Admin or Prompts Manager access required')
    }
    
    for (let i = 0; i < data.ids.length; i++) {
      await db.prompt.update({ where: { id: data.ids[i] }, data: { order: i } })
    }
    return { success: true }
  })
